import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Container,
  Badge,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import MessageIcon from '@mui/icons-material/Message';
import PersonIcon from '@mui/icons-material/Person';
import MessageApi from '../../api/MessageApi';
import MessageReplyDialog from '../../components/MessageReplyDialog/MessageReplyDialog';
import { ConversationDto } from '../../interfaces/MessageInterfaces';
import { DateTime } from 'luxon';
import './ManagerMessagesView.scss';

interface SearchFilters {
  /** Any part of the client's name — the server matches it against first and last name alike. */
  clientName: string;
  userEmail: string;
  messageManagerSeen: string; // 'any', 'seen', 'unseen'
  /**
   * 'any' | 'awaiting' | 'answered' — who spoke last, which is a different question from whether
   * anyone opened it. Kept as its own filter beside Seen rather than folded into it, so reading a
   * conversation stops hiding a client who is still waiting (Lloyd, Sep 1 2026).
   */
  awaitingReply: string;
}

const initialFilters: SearchFilters = {
  clientName: '',
  userEmail: '',
  messageManagerSeen: 'any',
  awaitingReply: 'any',
};

const ManagerMessagesView: React.FC = () => {
  const navigate = useNavigate();
  
  const [conversations, setConversations] = useState<ConversationDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<SearchFilters>(initialFilters);
  const [currentFrom, setCurrentFrom] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  

  
  const RESULTS_PER_PAGE = 20;
  // The conversation being replied to from this page, or null when the reply dialog is shut.
  const [replyFor, setReplyFor] = useState<{ guid: string; name: string } | null>(null);

  // The filters as query parameters. One name box rather than first/last: a first name typed into
  // the last-name box found nothing (Ashley, Aug 18), so the server matches either column.
  const buildParams = (searchFilters: SearchFilters, from: number, to: number) => {
    const params: any = { from, to };
    if (searchFilters.clientName.trim()) params.User_Name = searchFilters.clientName.trim();
    if (searchFilters.userEmail.trim()) params.User_Email = searchFilters.userEmail.trim();
    if (searchFilters.messageManagerSeen !== 'any') {
      params.Message_ManagerSeen = searchFilters.messageManagerSeen === 'seen';
    }
    if (searchFilters.awaitingReply !== 'any') {
      params.AwaitingReply = searchFilters.awaitingReply === 'awaiting';
    }
    return params;
  };

  const fetchConversations = useCallback(async (from = 0, searchFilters = filters, appendResults = false) => {
    if (!appendResults) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);

    try {
      const params = buildParams(searchFilters, from, from + RESULTS_PER_PAGE);

      const fetchedConversations = await MessageApi.getConversations(params);

      if (appendResults) {
        setConversations(prev => [...prev, ...fetchedConversations]);
      } else {
        setConversations(fetchedConversations);
      }

      setHasMoreData(fetchedConversations.length === RESULTS_PER_PAGE);
      setCurrentFrom(from);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch conversations');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filters]);

  const handleFilterChange = (filterName: keyof SearchFilters, value: string) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const handleSearch = () => {
    setCurrentFrom(0);
    setHasSearched(true);
    fetchConversations(0, filters, false);
  };

  const handleLoadMore = () => {
    if (hasMoreData && !loadingMore) {
      const nextFrom = currentFrom + RESULTS_PER_PAGE;
      fetchConversations(nextFrom, filters, true);
    }
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setCurrentFrom(0);
    setHasMoreData(false);
    setHasSearched(false);
    fetchConversations(0, initialFilters, false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const openConversation = (conversation: ConversationDto) => {
    navigate(`/message?User_guid=${conversation.lastMessage?.User.User_guid}`);
  };



  const formatMessageTime = (timestamp: string | Date) => {
    const dt = DateTime.fromJSDate(new Date(timestamp));
    // Luxon, not a JS Date: it takes format options, and the locale is set on the DateTime.
    return dt.toRelative() || dt.setLocale('en-US').toLocaleString(DateTime.DATETIME_SHORT);
  };

  const formatLastMessagePreview = (lastMessage: string | null) => {
    if (!lastMessage) return 'No messages';
    const preview = lastMessage.substring(0, 100);
    return preview.length < lastMessage.length ? `${preview}...` : preview;
  };

  useEffect(() => {
    fetchConversations();
  }, []);

  /*
   * Keep the list current without a refresh (Lloyd, 14 Oct 2026: "The system should constantly be
   * updating with unanswered messages remaining on the top"). Every minute it reloads the rows
   * already on screen, quietly: no spinner, and not while a reply is open, so nothing jumps under
   * someone mid-reply. The order itself comes from the server.
   */
  const conversationCount = conversations.length;
  useEffect(() => {
    if (replyFor) return undefined;
    const timer = window.setInterval(async () => {
      try {
        const rows = Math.max(conversationCount, RESULTS_PER_PAGE);
        setConversations(await MessageApi.getConversations(buildParams(filters, 0, rows)));
      } catch (err) {
        console.error('Background refresh of conversations failed:', err);
      }
    }, 60_000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replyFor, conversationCount, filters]);

  /** "text", "portal" or "email", from how the last message travelled. */
  const channelLabel = (conversation: ConversationDto): string | null => {
    const channels = conversation.lastMessage?.Message.Message_Channels ?? [];
    if (channels.includes('text')) return 'Text';
    if (channels.includes('email') && !channels.includes('portal')) return 'Email';
    if (channels.includes('portal')) return 'Portal';
    return null;
  };

  const clientName = (conversation: ConversationDto): string =>
    `${conversation.user.User_FirstName ?? ''} ${conversation.user.User_LastName ?? ''}`.trim();

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Message Conversations
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Every client text and portal message. Unanswered conversations stay at the top, longest
          waiting first, and the list updates every minute. Use Reply to answer with a drafted reply;
          it is recorded on the client's cases.
        </Typography>
        <MessageReplyDialog
          open={replyFor !== null}
          client={replyFor}
          onClose={() => setReplyFor(null)}
          onSent={() => fetchConversations(0, filters, false)}
        />

        {/* Search Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Search Users
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <TextField
              label="Client Name"
              placeholder="Any part of the name"
              value={filters.clientName}
              onChange={(e) => handleFilterChange('clientName', e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              size="small"
              sx={{ minWidth: 260, flex: 1 }}
            />
            
            <TextField
              label="User Email"
              value={filters.userEmail}
              onChange={(e) => handleFilterChange('userEmail', e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              size="small"
              sx={{ minWidth: 250, flex: 1 }}
            />
            
            <FormControl variant="outlined" size="small" sx={{ minWidth: 120, flex: 1 }}>
              <InputLabel>Seen</InputLabel>
              <Select
                value={filters.messageManagerSeen}
                onChange={(e) => handleFilterChange('messageManagerSeen', e.target.value)}
                label="Seen"
              >
                <MenuItem value="any">Any</MenuItem>
                <MenuItem value="seen">Seen</MenuItem>
                <MenuItem value="unseen">Unseen</MenuItem>
              </Select>
            </FormControl>

            <FormControl variant="outlined" size="small" sx={{ minWidth: 150, flex: 1 }}>
              <InputLabel>Reply</InputLabel>
              <Select
                value={filters.awaitingReply}
                onChange={(e) => handleFilterChange('awaitingReply', e.target.value)}
                label="Reply"
              >
                <MenuItem value="any">Any</MenuItem>
                <MenuItem value="awaiting">Awaiting reply</MenuItem>
                <MenuItem value="answered">Answered</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              size="small"
              disabled={loading}
            >
              Search Messages
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              size="small"
            >
              Clear Filters
            </Button>
          </Box>
        </Paper>

        {/* Results */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : conversations.length === 0 && hasSearched ? (
          <Alert severity="info">
            No conversations found matching your search criteria.
          </Alert>
        ) : conversations.length === 0 ? (
          <Alert severity="info">
            No message conversations yet.
          </Alert>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Last Message</TableCell>
                    <TableCell>Last Activity</TableCell>
                    <TableCell>Unseen</TableCell>
                    <TableCell>Reply</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {conversations.map((conversation) => (
                    <TableRow 
                      key={conversation.lastMessage?.User.User_guid}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openConversation(conversation)}
                    >
                      <TableCell>
                        <Typography variant="subtitle2">
                          {conversation.lastMessage?.User.User_FirstName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {conversation.lastMessage?.User.User_LastName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {/* Off the conversation, not off the last message: the message DTO carries
                              no email, which is why this cell was commented out and empty. */}
                          {conversation.user.User_Email || 'None'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography variant="body2" sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap' 
                        }}>
                          {formatLastMessagePreview(conversation.lastMessage?.Message.Message_Body || null)}
                        </Typography>
                        {channelLabel(conversation) && (
                          <Chip size="small" variant="outlined" label={channelLabel(conversation)} sx={{ mt: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        {conversation.lastMessage?.Message.Message_CreatedAt ? formatMessageTime(conversation.lastMessage.Message.Message_CreatedAt) : 'Never'}
                      </TableCell>
                      <TableCell>
                        {conversation.unseenCount > 0 ? (
                          <Badge badgeContent={conversation.unseenCount} color="error">
                            <MessageIcon />
                          </Badge>
                        ) : (
                          <MessageIcon color="disabled" />
                        )}
                      </TableCell>
                      <TableCell>
                        {/* Derived from who sent the last message, so reading cannot clear it —
                            only replying can. See ConversationDto.awaitingReply. */}
                        {conversation.awaitingReply ? (
                          <Chip
                            size="small"
                            color="warning"
                            label={
                              conversation.lastMessage?.Message.Message_CreatedAt
                                ? `Waiting ${formatMessageTime(conversation.lastMessage.Message.Message_CreatedAt).replace(/ ago$/, '')}`
                                : 'Awaiting reply'
                            }
                          />
                        ) : conversation.resurfacing?.state === 'no-response-from-client' ? (
                          <Chip
                            size="small"
                            color="default"
                            variant="outlined"
                            label="No response from client"
                            title={conversation.resurfacing.explanation}
                          />
                        ) : conversation.resurfacing?.state === 'resurfaced' ? (
                          /* We answered, the client did not, and the rung has elapsed. Chasing is
                             ours to do, so this reads as an action rather than a status. */
                          <Chip
                            size="small"
                            color="info"
                            label={
                              conversation.resurfacing.rounds > 1
                                ? `Chase again (${conversation.resurfacing.rounds})`
                                : 'Chase'
                            }
                            title={conversation.resurfacing.explanation}
                          />
                        ) : (
                          <Chip size="small" variant="outlined" label="Answered" />
                        )}
                        {/* What the system has already done, beside what staff still have to.
                            Shown next to the status rather than folded into the chase label so
                            nobody reads an automatic re-send as somebody having picked this up. */}
                        {(conversation.clientResendCount ?? 0) > 0 && (
                          <Chip
                            size="small"
                            variant="outlined"
                            sx={{ ml: 0.5 }}
                            label={`Re-sent ${conversation.clientResendCount}x`}
                            title="The client was sent our message again automatically."
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<MessageIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            openConversation(conversation);
                          }}
                        >
                          View
                        </Button>
                        <Button
                          variant={conversation.awaitingReply ? 'contained' : 'text'}
                          size="small"
                          sx={{ ml: 1 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setReplyFor({ guid: conversation.user.User_guid, name: clientName(conversation) });
                          }}
                        >
                          Reply
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Load More Button */}
            {hasMoreData && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  startIcon={loadingMore ? <CircularProgress size={16} /> : null}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </Button>
              </Box>
            )}
          </>
        )}


      </Container>
    </>
  );
};

export default ManagerMessagesView;